import React from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Wand2, Globe } from 'lucide-react';
import { CampaignPagePreview } from '@/components/campaign-page-preview';
import { PillTag } from '@/components/pill-tag';
import { fadeUp } from '@/constants/motion';
/**
 * Marketing showcase for the three primary campaign modes — used on the
 * landing page, /features, and /pricing. Same component everywhere so the
 * pitch is consistent: showroom (product gallery), auto-store (OS-aware
 * redirect), custom theme (35 themes × 42 animations).
 *
 * Showroom + custom-theme tiles iframe-render the public preview route at
 * phone-frame proportions; auto-store is rendered as a custom mockup
 * because the live route is a 302 with no public page (showing a redirect
 * isn't visually compelling on a marketing surface).
 */
export const CampaignModesSpotlight = ({ eyebrow = 'Campaign modes', title = 'One QR, three rendered pages', subtitle = 'Pick the mode that matches the moment. Every page picks up your brand colours, fonts, and animation.', className, }) => {
    return (<motion.section className={'border-t bg-muted/20 ' + (className ?? '')} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }}>
            <div className="mx-auto max-w-6xl px-4 py-14 md:py-20">
                <motion.div className="space-y-3 text-center" variants={fadeUp} transition={{ duration: 0.5, ease: 'easeOut' }}>
                    <PillTag label={eyebrow}/>
                    <h2 className="text-2xl font-semibold md:text-3xl">{title}</h2>
                    <p className="mx-auto max-w-2xl text-sm text-muted-foreground md:text-base">{subtitle}</p>
                </motion.div>

                <div className="mt-12 grid gap-10 md:grid-cols-3 items-end justify-items-center">
                    <ModeCard icon={<Smartphone className="h-4 w-4"/>} eyebrow="Showroom" caption="Product gallery" body="Cards-based catalogue. Every tracking link renders with image + price; tap a card opens a detail dialog with a single CTA." delay={0}>
                        <CampaignPagePreview design="industrial" pattern="blueprint" mode="showroom" caption="Industrial · Blueprint"/>
                    </ModeCard>

                    <ModeCard icon={<Globe className="h-4 w-4"/>} eyebrow="Auto-store" caption="Smart redirect" body="iOS scans go to the App Store, Android to Play, desktop lands on the web. One QR, three destinations — every click is attributed." delay={0.07}>
                        <AutoStoreMockup />
                    </ModeCard>

                    <ModeCard icon={<Wand2 className="h-4 w-4"/>} eyebrow="Custom theme" caption="35 themes × 42 animations" body="Pick a designer theme or save your own. Mix any animated background — aurora, glitch, fireworks, neo-brutal, more." delay={0.14}>
                        <CampaignPagePreview design="neo-brutal" pattern="neo-brutal" mode="classic" caption="Neo-Brutal · Blocks"/>
                    </ModeCard>
                </div>
            </div>
        </motion.section>);
};
function ModeCard({ icon, eyebrow, caption, body, delay = 0, children }) {
    return (<motion.div className="flex flex-col items-center gap-5 text-center" variants={fadeUp} transition={{ duration: 0.55, delay: 0.05 + delay }}>
            {children}
            <div className="space-y-2 max-w-xs">
                <div className="inline-flex items-center gap-1.5 rounded-full border bg-background px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {icon}
                    {eyebrow}
                </div>
                <h3 className="text-base font-semibold">{caption}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
        </motion.div>);
}
/**
 * Auto-store visual mockup. The actual auto-store route is a 302 — there's
 * no public HTML page to iframe — so we draw a phone-frame with the three
 * platform branches stacked. Conveys the "one QR → smart routing" pitch
 * without lying about what the user sees.
 */
function AutoStoreMockup() {
    return (<div className="flex flex-col items-center gap-3">
            <div className="relative aspect-[9/19] w-full max-w-[260px] overflow-hidden rounded-[2.4rem] border-[6px] border-neutral-900 bg-neutral-900 shadow-2xl dark:border-neutral-700">
                <div className="absolute left-1/2 top-1.5 z-10 h-3.5 w-16 -translate-x-1/2 rounded-full bg-neutral-900"/>
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-indigo-50 via-white to-pink-50 px-5 py-8 dark:from-indigo-950 dark:via-neutral-950 dark:to-pink-950">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Smart redirect</div>
                    <div className="h-12 w-12 animate-pulse rounded-full bg-primary/15 ring-2 ring-primary/40"/>
                    <div className="text-xs font-medium text-foreground">Detecting device…</div>

                    <div className="mt-4 w-full space-y-2 text-left">
                        <PlatformRow label="iOS" target="App Store" highlight/>
                        <PlatformRow label="Android" target="Google Play"/>
                        <PlatformRow label="Desktop" target="Fallback URL"/>
                    </div>

                    <div className="mt-2 text-[10px] text-muted-foreground">Same scan record · click attributed</div>
                </div>
            </div>
            <figcaption className="text-center">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Routing demo</div>
                <div className="text-sm font-medium">iOS · Android · Desktop</div>
            </figcaption>
        </div>);
}
function PlatformRow({ label, target, highlight }) {
    return (<div className={'flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-[11px] ' +
            (highlight ? 'border-primary/60 bg-primary/5' : 'border-border/60 bg-background/60')}>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
            <span className="font-medium text-foreground">{target}</span>
        </div>);
}
